import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ErrorBoundary } from '../components/ErrorBoundary';
import { SuccessNotificationProvider } from '../components/SuccessNotification';
import { AuthProvider } from '../features/auth/AuthProvider';

import { createQueryClient } from './query-client';

interface AppProvidersProps {
  children: ReactNode;
  /** Injected by tests so each case gets an isolated cache. */
  queryClient?: QueryClient;
}

/**
 * Cross-cutting providers.
 *
 * The error boundary sits outermost so a failure inside a provider still
 * renders a recoverable message rather than a blank page. Authentication sits
 * inside the query client because its bootstrap refresh is what makes
 * authenticated queries possible.
 */
export function AppProviders({ children, queryClient }: AppProvidersProps) {
  const [client] = useState(() => queryClient ?? createQueryClient());

  return (
    <ErrorBoundary>
      <QueryClientProvider client={client}>
        <AuthProvider>
          <SuccessNotificationProvider>{children}</SuccessNotificationProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
